import { components, parseApiError } from "@appretio/api";
import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import {
  Dialog,
  MultiSelect,
  Note,
  type Option,
  Toast
} from "@appretio/ui-kit";
import { useEffect, useMemo, useState } from "react";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import {
  type GenerateAlgorithmicForecastResult,
  useGenerateAlgorithmicForecast
} from "../../hooks/useGenerateAlgorithmicForecast";
import {
  getModelLabel,
  isAlgorithmicModel,
  MODEL_OPTIONS
} from "../../utils/algorithmicProducer";
import { getDisplayLabel } from "../../utils/summary/summaryPageHelpers";
import type { ForecastTableControlValues } from "../tableControls/forecastTableControlValues";
import { AdvancedOptionsSection } from "./AdvancedOptionsSection";
import * as styles from "./AlgorithmicForecastDialog.css";
import type { AlgorithmicForecastLoad } from "./algorithmicForecastLoad";
import {
  buildKpiOptions,
  DEFAULT_KPIS,
  DEFAULT_MODELS,
  findOption,
  isForecastableKpi
} from "./algorithmicForecastOptions";
import { deriveForecastDefaults } from "./deriveForecastDefaults";
import {
  buildGridWeekOptions,
  narrowRangeWeekOptions
} from "./gridWeekOptions";
import {
  producedRows,
  summarizeGenerateOutputs
} from "./summarizeGenerateOutputs";
import {
  describeMissingWeekSource,
  type GenerateInputErrors,
  validateGenerateInputs
} from "./validateGenerateInputs";

type ForecastableKpi = components["schemas"]["ForecastableKpi"];
type AlgorithmicModel = components["schemas"]["AlgorithmicModel"];

export interface AlgorithmicForecastDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Setter for the dialog's open state. */
  onOpenChange: (open: boolean) => void;
  /** The page's current dimension filters — sent through unchanged as the request's `context`. */
  context: ForecastContextSelectedOptions;
  /** Where the frame's history stops (`editable_week_from`) — caps the default training range. */
  editableWeekFrom: number | null | undefined;
  /** The plan's forecast boundary (`forecast_week_from`) — the default horizon counts its fiscal years from here. */
  forecastWeekFrom: number | null | undefined;
  /**
   * Distinct fiscal years of the loaded grid's rows — the week pickers
   * span exactly what the grid shows (`buildGridWeekOptions`).
   */
  fiscalYears: string[];
  /** The page's week-label toggle — the pickers spell weeks the way the grid headers do. */
  weekLabels: ForecastTableControlValues["weekLabels"];
  /**
   * Two fields are read: `weekPeriodMapping` supplies which weeks each of
   * `fiscalYears` really has (454-restated calendar) for the
   * training-range/forecast-horizon pickers; `metricDisplayMapping` labels
   * the KPI multi-select with the tenant's own metric names.
   */
  metaData: ForecastMetaDataType | undefined;
  /** Called on any success, including a partial one, with one load per requested model — its response, rows or none, and its failures. The page merges the rows into the grid beside the loads it already holds. */
  onLoaded: (loads: AlgorithmicForecastLoad[]) => void;
}

/**
 * The "Load algorithmic forecast" dialog (DAS-6566). Models + KPIs sit in the
 * body; everything that only shapes how the forecast is computed is collapsed
 * behind {@link AdvancedOptionsSection}.
 */
export function AlgorithmicForecastDialog({
  open,
  onOpenChange,
  context,
  editableWeekFrom,
  forecastWeekFrom,
  fiscalYears,
  weekLabels,
  metaData,
  onLoaded
}: AlgorithmicForecastDialogProps) {
  const [toast, setToast] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({ open: false, variant: "success", title: "" });

  const [models, setModels] = useState<AlgorithmicModel[]>(DEFAULT_MODELS);
  const [kpis, setKpis] = useState<ForecastableKpi[]>(DEFAULT_KPIS);
  const [trainWeekFrom, setTrainWeekFrom] = useState<Option>();
  const [trainWeekTo, setTrainWeekTo] = useState<Option>();
  const [forecastWeekTo, setForecastWeekTo] = useState<Option>();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [errors, setErrors] = useState<GenerateInputErrors>({});

  const { mutateAsync: generate, isPending } = useGenerateAlgorithmicForecast();

  const fiscalWeekOptions = useMemo(
    () =>
      buildGridWeekOptions(
        fiscalYears,
        metaData?.weekPeriodMapping,
        weekLabels
      ),
    [fiscalYears, metaData, weekLabels]
  );
  const rangeWeekOptions = narrowRangeWeekOptions(
    fiscalWeekOptions,
    {
      trainFrom: trainWeekFrom,
      trainTo: trainWeekTo,
      forecastTo: forecastWeekTo
    },
    {
      forecastFrom: editableWeekFrom ?? undefined,
      historyEndsBefore: editableWeekFrom ?? undefined
    }
  );
  const kpiOptions = useMemo(() => buildKpiOptions(metaData), [metaData]);
  const missingWeekSource = describeMissingWeekSource({
    hasWeekCalendar: metaData?.weekPeriodMapping != null,
    fiscalYears,
    editableWeekFrom,
    forecastWeekFrom
  });

  // Reset when the dialog opens — mirrors CreateCubeModal, and depends only on
  // `open` so a metadata query resolving late (a separate query from the one
  // that gates the toolbar button) can't clobber a selection already in progress.
  useEffect(() => {
    if (!open) return;
    setModels(DEFAULT_MODELS);
    setKpis(DEFAULT_KPIS);
    setIsAdvancedExpanded(false);
    setErrors({});
    setTrainWeekFrom(undefined);
    setTrainWeekTo(undefined);
    setForecastWeekTo(undefined);
  }, [open]);

  // Fills the week defaults once fiscalWeekOptions is ready — separate from the
  // reset above so it can safely re-run if metadata resolves after the dialog
  // opens, without re-triggering while the three fields are already set.
  useEffect(() => {
    if (!open || trainWeekFrom || trainWeekTo || forecastWeekTo) return;
    if (fiscalWeekOptions.length === 0) return;

    const defaults = deriveForecastDefaults(
      { editableWeekFrom, forecastWeekFrom },
      fiscalWeekOptions.map((option) => ({
        yearWeek: Number(option.value),
        fiscalYear: option.fiscalYear
      }))
    );
    // No defaults means the planner has to pick: put the empty pickers in view.
    if (!defaults) setIsAdvancedExpanded(true);
    setTrainWeekFrom(findOption(fiscalWeekOptions, defaults?.trainWeekFrom));
    setTrainWeekTo(findOption(fiscalWeekOptions, defaults?.trainWeekTo));
    setForecastWeekTo(findOption(fiscalWeekOptions, defaults?.forecastWeekTo));
  }, [
    open,
    editableWeekFrom,
    forecastWeekFrom,
    fiscalWeekOptions,
    trainWeekFrom,
    trainWeekTo,
    forecastWeekTo
  ]);

  function handleGenerateSuccess({
    outputs,
    failed
  }: GenerateAlgorithmicForecastResult) {
    const modelLabels = outputs.map(({ model }) => getModelLabel(model));
    const outcomes = summarizeGenerateOutputs(
      outputs.map(({ output }) => output),
      modelLabels,
      (metricName) => getDisplayLabel(metricName, metaData),
      models.length > 1
    );
    // A failed request precedes the per-KPI lines: the planner sees first
    // which model is missing altogether.
    const failures = [
      ...failed.map(
        ({ model }) => `${getModelLabel(model)} · the request failed`
      ),
      ...outcomes.flatMap((outcome) => outcome.failures)
    ];
    if (!outcomes.some((outcome) => producedRows(outcome.output))) {
      setToast({
        open: true,
        variant: "error",
        title: "Algorithmic forecast failed",
        description: failures.join("; ")
      });
      return;
    }

    onLoaded(
      outcomes.map((outcome) => ({ ...outcome, groundTruthLabel: "Actuals" }))
    );
    onOpenChange(false);
    setToast(
      failures.length > 0
        ? {
            open: true,
            variant: "error",
            title: "Some forecasts failed to load",
            description: failures.join("; ")
          }
        : {
            open: true,
            variant: "success",
            title: "Algorithmic forecast generated",
            description: `${modelLabels.join(", ")} · ${kpis
              .map((kpi) => getDisplayLabel(kpi, metaData))
              .join(", ")}`
          }
    );
  }

  function handleTrainWeekFromChange(next: Option | undefined) {
    setTrainWeekFrom(next);
    // The range rule reports on the "to" field, so its error clears too.
    setErrors((prev) => ({
      ...prev,
      trainWeekFrom: undefined,
      trainWeekTo: undefined
    }));
  }

  function handleTrainWeekToChange(next: Option | undefined) {
    setTrainWeekTo(next);
    // The horizon rule compares against this field, so its error clears too.
    setErrors((prev) => ({
      ...prev,
      trainWeekTo: undefined,
      forecastWeekTo: undefined
    }));
  }

  function handleForecastWeekToChange(next: Option | undefined) {
    setForecastWeekTo(next);
    setErrors((prev) => ({ ...prev, forecastWeekTo: undefined }));
  }

  async function handleConfirm() {
    const nextErrors = validateGenerateInputs({
      models,
      kpis,
      trainWeekFrom,
      trainWeekTo,
      forecastWeekTo
    });
    setErrors(nextErrors);
    if (
      nextErrors.trainWeekFrom ||
      nextErrors.trainWeekTo ||
      nextErrors.forecastWeekTo
    ) {
      setIsAdvancedExpanded(true);
    }
    if (Object.keys(nextErrors).length > 0) return;
    if (!trainWeekFrom || !trainWeekTo || !forecastWeekTo) return;

    try {
      const result = await generate({
        context: context ?? {},
        models,
        ground_truth: "actuals",
        train_week_from: Number(trainWeekFrom.value),
        train_week_to: Number(trainWeekTo.value),
        forecast_week_to: Number(forecastWeekTo.value),
        kpis
      });
      handleGenerateSuccess(result);
    } catch (error) {
      // The api client rejects non-2xx with the envelope's curated message on
      // `cause.statusMessage`; parseApiError shows it for 4xx (a guard or
      // validation rejection the planner can act on) and stays generic on 5xx.
      const { title, description } =
        error instanceof Error
          ? parseApiError(error, "Failed to generate algorithmic forecast")
          : {
              title: "Failed to generate algorithmic forecast",
              description: undefined
            };
      setToast({ open: true, variant: "error", title, description });
    }
  }

  return (
    <>
      <Toast
        open={toast.open}
        setOpen={(next) => setToast((prev) => ({ ...prev, open: next }))}
        variant={toast.variant}
        title={toast.title}
        description={toast.description}
      />
      <Dialog
        open={open}
        title="Load Algorithmic Forecast"
        onOpenChange={onOpenChange}
        confirmText="Generate"
        cancelText="Cancel"
        onSubmit={() => void handleConfirm()}
        isLoading={isPending}
        confirmDisabled={fiscalWeekOptions.length === 0}
      >
        <div className={styles.formContainer}>
          <MultiSelect
            label="Models"
            options={MODEL_OPTIONS}
            value={models}
            onChange={(next) => {
              setModels(next.filter(isAlgorithmicModel));
              setErrors((prev) => ({ ...prev, models: undefined }));
            }}
            error={errors.models}
            disabled={isPending}
            modalPopover
            size="md"
          />

          <MultiSelect
            label="KPIs"
            options={kpiOptions}
            value={kpis}
            onChange={(next) => {
              setKpis(next.filter(isForecastableKpi));
              setErrors((prev) => ({ ...prev, kpis: undefined }));
            }}
            error={errors.kpis}
            disabled={isPending}
            modalPopover
            size="md"
          />

          {missingWeekSource && (
            <Note variant="warning" size="compact" text={missingWeekSource} />
          )}

          <AdvancedOptionsSection
            expanded={{
              value: isAdvancedExpanded,
              onChange: setIsAdvancedExpanded
            }}
            weeks={{
              options: rangeWeekOptions,
              trainFrom: trainWeekFrom,
              onTrainFromChange: handleTrainWeekFromChange,
              trainTo: trainWeekTo,
              onTrainToChange: handleTrainWeekToChange,
              forecastTo: forecastWeekTo,
              onForecastToChange: handleForecastWeekToChange
            }}
            errors={errors}
            isPending={isPending}
          />

          {/* Trimmed from the SPEC mock's pin-7 note: its "copy it into
                your version to keep it" clause points at the copy dialog —
                restore the full wording together with DAS-6570. */}
          <Note
            variant="info"
            size="compact"
            text="Trains per KPI on the displayed context only. The loaded forecast is view-only and not saved to your version."
          />
        </div>
      </Dialog>
    </>
  );
}
